// ═══ AI EXIT VERDICT MODAL — swipe-left "★ AI Exit" trigger ═══════════════
// Extracted from SwipeCards.js (iter 96). Calls /api/elite/exit/{pid} and
// renders HOLD / SCALE_OUT / EXIT verdict. Handles both option positions
// (theta/IV crush metrics) and crypto positions (support/resistance).
import { useEffect, useState } from "react";
import axios from "axios";
import { Skel } from "./Skel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AiExitVerdictModal({ position, onClose }) {
  const [verdict, setVerdict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const pid = position?._id || position?.id || position?.positionId || position?.symbol;
    if (!pid) { setLoading(false); setError(true); return; }
    axios.get(`${API}/elite/exit/${encodeURIComponent(pid)}`).then(r => {
      setVerdict(r.data);
      setLoading(false);
    }).catch(() => { setError(true); setLoading(false); });
  }, [position]);

  const actionColor = verdict?.action === "EXIT" ? "#f87171"
                    : verdict?.action === "SCALE_OUT" ? "#fbbf24"
                    : verdict?.action === "HOLD" ? "#4ade80" : "#60a5fa";
  const isCrypto = verdict?.assetClass === "crypto";

  return (
    <div
      data-testid="ai-exit-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 20010,
        background: "rgba(0,0,0,0.7)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480,
          borderTopLeftRadius: 22, borderTopRightRadius: 22,
          background: "var(--bg, #060a12)", border: "1px solid rgba(167,139,250,0.22)",
          padding: "22px 20px 32px",
          maxHeight: "85vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#a78bfa", animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2, color: "#a78bfa" }}>
            OMEGA ZI AI · {isCrypto ? "CRYPTO" : "OPTIONS"} EXIT VERDICT
          </span>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", color: "#8b949e", fontSize: 20, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ fontSize: 18, fontWeight: 800, color: "#e6edf3", marginBottom: 4 }}>
          {position.ticker} {position.strike ? `$${position.strike}${position.side?.[0] || ""}` : ""}
        </div>
        <div style={{ fontSize: 11, color: "#8b949e", marginBottom: 14 }}>
          {isCrypto
            ? `Spot $${Number(verdict?.current_price || 0).toFixed(2)} · Entry $${Number(verdict?.entry_price || 0).toFixed(2)}`
            : `Entry $${(position.avgCost || 0).toFixed(2)} · ${position.quantity} contract${position.quantity === 1 ? "" : "s"}`}
        </div>

        {loading && <><Skel w="100%" h={80} mb={8} radius={12} /><Skel w="100%" h={60} mb={0} radius={12} /></>}
        {!loading && error && <div style={{ padding: 20, textAlign: "center", color: "#8b949e", fontSize: 12 }}>AI verdict unavailable for this position. Try the main Elite view.</div>}

        {!loading && verdict && !error && (
          <>
            <div data-testid="ai-exit-action" style={{
              padding: 16, borderRadius: 14, marginBottom: 12,
              background: `${actionColor}15`, border: `1px solid ${actionColor}44`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: actionColor, letterSpacing: 1 }}>{verdict.action}</span>
                {verdict.confidence != null && (
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, color: "#8b949e" }}>Confidence</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: "#e6edf3" }}>{verdict.confidence}%</div>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 13, color: "#c9d1d9", lineHeight: 1.5 }}>{verdict.rationale}</div>
            </div>

            {!isCrypto && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 12 }}>
                <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)" }}>
                  <div style={{ fontSize: 9, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>Target</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#e6edf3" }}>${verdict.target}</div>
                </div>
                <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)" }}>
                  <div style={{ fontSize: 9, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>Unrealized</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: (verdict.gain_pct || 0) >= 0 ? "#4ade80" : "#f87171" }}>
                    {(verdict.gain_pct || 0) >= 0 ? "+" : ""}{verdict.gain_pct}%
                  </div>
                </div>
                <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)" }}>
                  <div style={{ fontSize: 9, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>Theta burn (5d)</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#f87171" }}>−${verdict.theta_burn_5d}</div>
                </div>
                <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)" }}>
                  <div style={{ fontSize: 9, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>IV crush risk</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: verdict.iv_crush_risk_pct >= 50 ? "#f87171" : "#8b949e" }}>
                    {verdict.iv_crush_risk_pct}%
                  </div>
                </div>
              </div>
            )}

            {isCrypto && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 12 }}>
                <div style={{ padding: 10, borderRadius: 10, background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.14)" }}>
                  <div style={{ fontSize: 9, color: "#4ade80", letterSpacing: 1, textTransform: "uppercase" }}>Support</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#e6edf3" }}>${verdict.support?.toLocaleString()}</div>
                </div>
                <div style={{ padding: 10, borderRadius: 10, background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.14)" }}>
                  <div style={{ fontSize: 9, color: "#f87171", letterSpacing: 1, textTransform: "uppercase" }}>Resistance</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#e6edf3" }}>${verdict.resistance?.toLocaleString()}</div>
                </div>
                <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)" }}>
                  <div style={{ fontSize: 9, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>30d Trend</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: (verdict.change_30d_pct || 0) >= 0 ? "#4ade80" : "#f87171" }}>
                    {(verdict.change_30d_pct || 0) >= 0 ? "+" : ""}{verdict.change_30d_pct}%
                  </div>
                </div>
                <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)" }}>
                  <div style={{ fontSize: 9, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>Gain/Loss</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: (verdict.gain_pct || 0) >= 0 ? "#4ade80" : "#f87171" }}>
                    {(verdict.gain_pct || 0) >= 0 ? "+" : ""}{verdict.gain_pct}%
                  </div>
                </div>
              </div>
            )}

            {verdict.trail_pct != null && (
              <div style={{ fontSize: 10, color: "#636e7b", marginTop: 4, fontStyle: "italic", textAlign: "center" }}>
                Dynamic trail recommendation: {verdict.trail_pct}%{!isCrypto && verdict.dte_now != null ? ` · DTE ${verdict.dte_now}` : ""}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
